import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDemo } from '@/hooks/useDemo';
import { useSession } from '@/lib/hooks/useSession';
import { supabase } from '@/integrations/supabase/client';
import { useTenantStore } from '@/stores/tenantStore';

export interface RestaurantData {
  restaurant_id: string;
  subdomain: string;
  name: string;
}

interface UseTenantResult {
  restaurantData: RestaurantData | null;
  loading: boolean;
  setRestaurant: (data: RestaurantData) => void;
  clearRestaurant: () => void;
}

interface RestaurantRow {
  id: string;
  name: string | null;
  subdomain: string;
  is_active?: boolean;
}

interface StaffRow {
  restaurant_id: string;
  restaurants: RestaurantRow | null;
}

const STORAGE_KEY = 'restaurant_data';

const resolvePathSubdomain = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const match = window.location.pathname.match(/^\/r\/([^/]+)/i);
  return match?.[1] ?? null;
};

const persistRestaurant = (data: RestaurantData): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[useTenant] failed to persist restaurant', error);
    }
  }
};

const readStoredRestaurant = (): RestaurantData | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<RestaurantData>;
    if (
      parsed &&
      typeof parsed.restaurant_id === 'string' &&
      typeof parsed.subdomain === 'string' &&
      typeof parsed.name === 'string'
    ) {
      return {
        restaurant_id: parsed.restaurant_id,
        subdomain: parsed.subdomain,
        name: parsed.name,
      };
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('[useTenant] failed to parse stored restaurant data', error);
    }
  }

  return null;
};

const isRestaurantRow = (value: unknown): value is RestaurantRow => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.subdomain === 'string' &&
    (candidate.name === null || typeof candidate.name === 'string')
  );
};

const isStaffRow = (value: unknown): value is StaffRow => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.restaurant_id === 'string' &&
    (candidate.restaurants === null || isRestaurantRow(candidate.restaurants))
  );
};

export const useTenant = (): UseTenantResult => {
  const [restaurantData, setRestaurantData] = useState<RestaurantData | null>(null);
  const [loading, setLoading] = useState(true);

  const { isDemo, demoSession } = useDemo();
  const session = useSession();
  const sessionTenant = session.tenant;

  const activeRestaurantId = useTenantStore((state) => state.activeRestaurantId);
  const activeSubdomain = useTenantStore((state) => state.activeSubdomain);
  const activeRestaurantName = useTenantStore((state) => state.activeRestaurantName);
  const setActiveTenant = useTenantStore((state) => state.setActiveTenant);
  const clearTenant = useTenantStore((state) => state.clearTenant);

  const applyRestaurant = useCallback(
    (data: RestaurantData, options?: { syncStore?: boolean; isDemo?: boolean }) => {
      setRestaurantData((current) => {
        const unchanged =
          current?.restaurant_id === data.restaurant_id &&
          current?.subdomain === data.subdomain &&
          current?.name === data.name;

        if (unchanged) {
          return current;
        }

        if (options?.syncStore !== false) {
          setActiveTenant(data.restaurant_id, data.subdomain, data.name, options?.isDemo ?? false);
        }

        persistRestaurant(data);
        return data;
      });
    },
    [setActiveTenant],
  );

  useEffect(() => {
    if (!activeRestaurantId || !activeSubdomain) {
      return;
    }

    const normalizedName = activeRestaurantName ?? activeSubdomain;
    const next: RestaurantData = {
      restaurant_id: activeRestaurantId,
      subdomain: activeSubdomain,
      name: normalizedName,
    };

    setRestaurantData((current) => {
      if (
        current &&
        current.restaurant_id === next.restaurant_id &&
        current.subdomain === next.subdomain &&
        current.name === next.name
      ) {
        return current;
      }

      persistRestaurant(next);
      return next;
    });
  }, [activeRestaurantId, activeSubdomain, activeRestaurantName]);

  useEffect(() => {
    let isMounted = true;

    const markLoaded = (): void => {
      if (isMounted) {
        setLoading(false);
      }
    };

    const loadFromActiveStore = (): boolean => {
      if (!activeRestaurantId || !activeSubdomain) {
        return false;
      }

      const normalizedName =
        activeRestaurantName ?? activeSubdomain ?? restaurantData?.name ?? 'Restaurant';

      applyRestaurant(
        {
          restaurant_id: activeRestaurantId,
          subdomain: activeSubdomain,
          name: normalizedName,
        },
        { syncStore: false, isDemo: false },
      );

      markLoaded();
      return true;
    };

    const loadRestaurantData = async (): Promise<void> => {
      setLoading(true);
      try {
        if (isDemo && demoSession) {
          const demoData: RestaurantData = {
            restaurant_id: demoSession.restaurant_id,
            subdomain: demoSession.subdomain,
            name: 'Demo Restaurant',
          };
          if (isMounted) {
            applyRestaurant(demoData, { isDemo: true });
          }
          return;
        }

        if (loadFromActiveStore()) {
          return;
        }

        const stored = readStoredRestaurant();
        if (stored) {
          const { data: restaurantRaw, error } = await supabase
            .from('restaurants')
            .select('id, name, subdomain')
            .eq('id', stored.restaurant_id)
            .eq('is_active', true)
            .single();

          if (!error && isRestaurantRow(restaurantRaw)) {
            if (isMounted) {
              applyRestaurant({
                restaurant_id: restaurantRaw.id,
                subdomain: restaurantRaw.subdomain,
                name: restaurantRaw.name ?? restaurantRaw.subdomain,
              });
            }
            return;
          }

          if (typeof window !== 'undefined') {
            window.localStorage.removeItem(STORAGE_KEY);
          }
        }

        const sessionResponse = await supabase.auth.getSession();
        const authSession = sessionResponse.data.session;

        if (authSession?.user) {
          const { data: staffRowsRaw, error: staffError } = await supabase
            .from('staff')
            .select(`
              restaurant_id,
              restaurants!inner(
                id,
                name,
                subdomain,
                is_active
              )
            `)
            .eq('auth_user_id', authSession.user.id)
            .eq('is_active', true)
            .eq('restaurants.is_active', true)
            .limit(1);

          if (!staffError && Array.isArray(staffRowsRaw)) {
            const staffEntry = staffRowsRaw.find(isStaffRow);
            const restaurant = staffEntry?.restaurants;

            if (isRestaurantRow(restaurant)) {
              if (isMounted) {
                applyRestaurant({
                  restaurant_id: restaurant.id,
                  subdomain: restaurant.subdomain,
                  name: restaurant.name ?? restaurant.subdomain,
                });
              }
              return;
            }
          }
        }

        const urlParams =
          typeof window !== 'undefined'
            ? new URLSearchParams(window.location.search)
            : new URLSearchParams();
        const explicitSubdomain = urlParams.get('r');

        const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
        const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';

        let resolvedSubdomain = resolvePathSubdomain();

        if (!resolvedSubdomain && explicitSubdomain) {
          resolvedSubdomain = explicitSubdomain;
        }

        if (!resolvedSubdomain && !isLocalhost && hostname.includes('.')) {
          const parts = hostname.split('.');
          if (parts.length > 2) {
            resolvedSubdomain = parts[0];
          }
        }

        if (!resolvedSubdomain && isLocalhost) {
          resolvedSubdomain = 'demo';
        }

        if (resolvedSubdomain) {
          const { data: restaurantRaw, error } = await supabase
            .from('restaurants')
            .select('id, name, subdomain')
            .eq('subdomain', resolvedSubdomain)
            .eq('is_active', true)
            .single();

          if (!error && isRestaurantRow(restaurantRaw)) {
            if (isMounted) {
              applyRestaurant({
                restaurant_id: restaurantRaw.id,
                subdomain: restaurantRaw.subdomain,
                name: restaurantRaw.name ?? restaurantRaw.subdomain,
              });
            }
            return;
          }

          if (!restaurantRaw && import.meta.env.DEV) {
            const fallback: RestaurantData = {
              restaurant_id: `dev-${resolvedSubdomain}`,
              subdomain: resolvedSubdomain,
              name: resolvedSubdomain,
            };

            if (isMounted) {
              applyRestaurant(fallback, { isDemo: false });
            }
            return;
          }
        }
      } catch (error) {
        console.error('Error loading restaurant data:', error);
        if (!activeRestaurantId) {
          clearTenant();
        }
      } finally {
        markLoaded();
      }
    };

    if (!loadFromActiveStore()) {
      loadRestaurantData().catch((error) => {
        if (import.meta.env.DEV) {
          console.warn('[useTenant] loadRestaurantData rejected', error);
        }
      });
    }

    return () => {
      isMounted = false;
    };
  }, [
    activeRestaurantId,
    activeSubdomain,
    activeRestaurantName,
    applyRestaurant,
    clearTenant,
    demoSession,
    isDemo,
    restaurantData?.name,
  ]);

  useEffect(() => {
    if (!sessionTenant) {
      return;
    }

    applyRestaurant(
      {
        restaurant_id: sessionTenant.id,
        subdomain: sessionTenant.slug,
        name: sessionTenant.name ?? sessionTenant.slug,
      },
      { isDemo: false },
    );

    setLoading(false);
  }, [applyRestaurant, sessionTenant]);

  const setRestaurant = useCallback(
    (data: RestaurantData): void => {
      applyRestaurant(data);
    },
    [applyRestaurant],
  );

  const clearRestaurant = useCallback((): void => {
    setRestaurantData(null);
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(STORAGE_KEY);
    }
    clearTenant();
  }, [clearTenant]);

  return useMemo(
    () => ({
      restaurantData,
      loading,
      setRestaurant,
      clearRestaurant,
    }),
    [restaurantData, loading, setRestaurant, clearRestaurant],
  );
};
